import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { AuthenticatedUser, UserRole } from '@/types';
import { setTokenAccessor } from '@/api/client';
import { determineRole, hasMinRole } from '@/utils/roles';
import { leiteZurAnmeldung } from '@/utils/loginUrl';

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1]!;
  const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

/**
 * Liest einen Fehler aus dem OAuth-Callback-Redirect (`?error=...`) und gibt
 * ihn als fertige Meldung zurueck, sonst `null`.
 */
function readCallbackError(): string | null {
  const callbackError = new URLSearchParams(window.location.search).get('error');
  return callbackError ? `Anmeldung fehlgeschlagen: ${decodeURIComponent(callbackError)}` : null;
}

// ═══════════════════════════════════════════════════════
// Auth Context
// ═══════════════════════════════════════════════════════

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthenticatedUser | null;
  error: string | null;
}

interface AuthContextType extends AuthState {
  logout: () => void;
  getToken: () => string | undefined;
  hasRole: (role: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ═══════════════════════════════════════════════════════
// Auth Provider (BFF — session via backend)
// ═══════════════════════════════════════════════════════

export function AuthProvider({ children }: { children: ReactNode }) {
  // Ein Fehler aus dem Callback-Redirect steht schon beim ersten Render in der
  // URL. Er geht deshalb direkt in den Startzustand, statt nachtraeglich im
  // Effekt gesetzt zu werden.
  const [state, setState] = useState<AuthState>(() => {
    const callbackError = readCallbackError();
    return {
      isAuthenticated: false,
      isLoading: callbackError === null,
      user: null,
      error: callbackError,
    };
  });
  const accessTokenRef = useRef<string | undefined>(undefined);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Holt eine frische Session vom Backend und plant die naechste Erneuerung.
    // Bewusst innerhalb des Effekts deklariert: ausserhalb wurde die Funktion
    // vor ihrer Deklaration verwendet, obwohl sie nirgends sonst gebraucht
    // wird.
    function refreshSession() {
      fetch('/api/v1/auth/session', { credentials: 'include' })
        .then((res) => {
          if (!res.ok) throw new Error('Refresh failed');
          return res.json();
        })
        .then((data: { data: { accessToken: string; expiresAt: number } }) => {
          accessTokenRef.current = data.data.accessToken;
          const msUntilExpiry = data.data.expiresAt - Date.now() - 30_000;
          if (msUntilExpiry > 0) {
            setTimeout(() => refreshSession(), msUntilExpiry);
          }
        })
        .catch(() => {
          accessTokenRef.current = undefined;
          setState({ isAuthenticated: false, isLoading: false, user: null, error: null });
        });
    }

    // Wire up token accessor for API client
    setTokenAccessor(() => accessTokenRef.current);

    // Listen for token refreshed by API interceptor (keeps ref in sync)
    const onTokenRefreshed = (e: Event) => {
      const { accessToken, expiresAt } = (e as CustomEvent).detail;
      accessTokenRef.current = accessToken;
      // Re-schedule next proactive refresh
      const msUntilExpiry = expiresAt - Date.now() - 30_000;
      if (msUntilExpiry > 0) {
        setTimeout(() => refreshSession(), msUntilExpiry);
      }
    };
    window.addEventListener('auth:token-refreshed', onTokenRefreshed);

    // Listen for unrecoverable auth failure
    const onUnauthorized = () => {
      accessTokenRef.current = undefined;
      setState({ isAuthenticated: false, isLoading: false, user: null, error: null });
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);

    // Check for error from callback redirect
    // Der Zustand dazu steht bereits (siehe useState oben) — hier wird nur
    // noch die URL bereinigt und der Session-Abruf uebersprungen.
    if (readCallbackError()) {
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Fetch session from backend (cookie-based)
    fetch('/api/v1/auth/session', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('No session');
        return res.json();
      })
      .then((data: { data: { accessToken: string; expiresAt: number } }) => {
        const token = data.data.accessToken;
        accessTokenRef.current = token;

        const claims = decodeJwtPayload(token);
        const { role, tenant, tenants } = determineRole(claims);

        setState({
          isAuthenticated: true,
          isLoading: false,
          user: {
            id: (claims.sub as string) || '',
            email: (claims.email as string) || '',
            name: (claims.name as string) || undefined,
            role,
            tenant,
            tenants,
          },
          error: null,
        });

        // Schedule token refresh before expiry
        const msUntilExpiry = data.data.expiresAt - Date.now() - 30_000;
        if (msUntilExpiry > 0) {
          setTimeout(() => refreshSession(), msUntilExpiry);
        }
      })
      .catch(() => {
        setState({ isAuthenticated: false, isLoading: false, user: null, error: null });
      });

    return () => {
      window.removeEventListener('auth:token-refreshed', onTokenRefreshed);
      window.removeEventListener('auth:unauthorized', onUnauthorized);
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.data?.logoutUrl) {
        window.location.href = data.data.logoutUrl;
        return;
      }
    } catch {
      /* fallback below */
    }
    window.location.href = '/';
  }, []);

  const getToken = useCallback(() => accessTokenRef.current, []);

  const hasRole = useCallback(
    (requiredRole: UserRole) => {
      if (!state.user) return false;
      return hasMinRole(state.user.role, requiredRole);
    },
    [state.user]
  );

  return (
    <AuthContext.Provider value={{ ...state, logout, getToken, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════
// Protected Route
// ═══════════════════════════════════════════════════════

export function ProtectedRoute({ children, requiredRole }: { children: ReactNode; requiredRole?: UserRole }) {
  const { isAuthenticated, isLoading, hasRole, error } = useAuth();
  const redirecting = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || error || redirecting.current) return;
    redirecting.current = true;
    // Redirect to backend OAuth login endpoint
    leiteZurAnmeldung();
  }, [isLoading, isAuthenticated, error]);

  if (isLoading) {
    return (
      <div className="min-h-screen app-background flex items-center justify-center">
        <div className="glass-surface p-8 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-surface-600 font-medium">Authentifizierung...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen app-background flex items-center justify-center">
        <div className="glass-surface p-8 rounded-2xl max-w-lg">
          <div className="flex flex-col items-center gap-4">
            <div className="text-error text-4xl font-bold">!</div>
            <h2 className="text-surface-900 font-bold text-lg">Anmeldung fehlgeschlagen</h2>
            <p className="text-surface-600 text-sm text-center">{error}</p>
            <button
              onClick={() => leiteZurAnmeldung('/admin')}
              className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen app-background flex items-center justify-center">
        <div className="glass-surface p-8 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-surface-600 font-medium">Weiterleitung zur Anmeldung...</span>
          </div>
        </div>
      </div>
    );
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default AuthProvider;
